import { Injectable } from "@angular/core";
import { LorenMetering } from "../../../../../features/loren-assets/interfaces/loren-metering.interface";
import { LOREN_DATE } from "../../../../constants/loren-date.constants";
import { LorenDateUtilsService } from "../../../../services/loren-date-utils-service/loren-date-utils.service";
import { LorenLineChartViewModel } from "../../interfaces/loren-line-chart-view-model.interface";
import { LorenLineChartPoint } from "../../interfaces/loren-line-chart-point.interface";
import { LorenLineChartSeriesItem } from "../../interfaces/loren-line-chart-series-item.interface";
import {
  LorenMetricCategories,
  LorenMetricKinds,
} from "../../../loren-asset-card/constants/loren-metric-types.constants";
import { LorenMeteringType } from "../../../../../features/loren-assets/interfaces/loren-metering-type";
import {
  countDays,
  DateRange,
  evaluateDateRange
} from "../../../../../common/forms/period-select/period-select.component";

@Injectable({
  providedIn: "root",
})
export class LorenLineChartUtilsService {
  static lineChartBGLocaleOptions = {
    months: [
      "Януари",
      "Февруари",
      "Март",
      "Април",
      "Май",
      "Юни",
      "Юли",
      "Август",
      "Септември",
      "Октомври",
      "Ноември",
      "Декември",
    ],
    shortMonths: [
      "Яну",
      "Фев",
      "Мар",
      "Апр",
      "Май",
      "Юни",
      "Юли",
      "Авг",
      "Сеп",
      "Окт",
      "Ное",
      "Дек",
    ],
    days: [
      "Неделя",
      "Понеделник",
      "Вторник",
      "Сряда",
      "Четвъртък",
      "Петък",
      "Събота",
    ],
    shortDays: ["Нед", "Пон", "Вто", "Сря", "Чет", "Пет", "Съб"],
    toolbar: {
      exportToSVG: "Изтеглете SVG",
      exportToPNG: "Изтеглете PNG",
      exportToCSV: "Изтеглете CSV",
      menu: "Изтеглете",
      selection: "Изберете секция",
      selectionZoom: "Увеличете секция",
      zoomIn: "Увеличете",
      zoomOut: "Намалете",
      pan: "Придвижете",
      reset: "Начален размер",
    },
  };

  static prepareLineChartViewModel(
    points: LorenLineChartPoint[],
    xAxisStep: number,
    period: DateRange,
    metering: LorenMetering,
    totalFalseState = null
  ): LorenLineChartViewModel {
    const metricIsBinary =
      LorenMetricKinds.get(metering.type) === LorenMetricCategories.BINARY;
    let dateFormat = LOREN_DATE.chartHourValues;

    if (!points) {
      return;
    }

    const [from, to] = evaluateDateRange(period);
    const days = countDays(from, to);

    if (days > 1 && days < 14) {
      dateFormat = LOREN_DATE.currentValueFormat;
    } else if (days >= 14) {
      dateFormat = LOREN_DATE.chartDateFormat;
    }

    const xAxisTicks: string[] = [];
    const totalOpen =
      totalFalseState !== null ? { totalOpen: totalFalseState } : {};

    return {
      xAxisTicks,
      lorenLineChartData: {
        name: `${metering.description}`,
        id: metering.id,
        format: dateFormat,
        series: points.map((x, index) => {
          const momentDate = LorenDateUtilsService.getMomentFromDate(x.time);
          if (index % xAxisStep === 0) {
            xAxisTicks.push(momentDate.format(dateFormat));
          }

          const seriesItem: LorenLineChartSeriesItem = {
            objectName: metering.description,
            max: x.max,
            min: x.min,
            notes: x.notes,
            anomaly: x.anomaly,
            x: new Date(momentDate.toDate()).getTime(),
            y: x.anomaly
              ? null
              : metricIsBinary
              ? LorenLineChartUtilsService.invertBinaryValues(
                  metering.type,
                  x.state
                )
              : x.value ?? null,
            yArray: x.anomaly ? [null, null] : [x.max, x.min],
          };

          return seriesItem;
        }),
        ...totalOpen,
      },
    };
  }

  static invertBinaryValues(meteringType: LorenMeteringType, value) {
    const shouldBeInverted = [
      LorenMeteringType.BINARY_SENSOR,
      LorenMeteringType.IGNITION_BINARY_SENSOR,
      LorenMeteringType.SMOKE_BINARY_SENSOR,
    ].some((type) => type === meteringType);

    return shouldBeInverted ? Number(!value) : Number(value);
  }
}
